/**
 * @fileoverview A lista dos agentes de IA: a foto, o nome, em quais fluxos cada
 * um está ("usado em") e as ações (editar ou ver, excluir); sem texto no ar, o
 * aviso (a IA usaria o texto padrão).
 * O agente que algum fluxo usa não sai: o servidor recusa e a tela já avisa.
 * Criar: pelo assistente (lê o site do negócio) ou em branco.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import { Eye, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { formatDate } from "../../../../utils/formatLocaleDate";
import { i18n } from "../../../../translate/i18n";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import useStyles from "../TriageManager/styles";
import useAgentStyles from "./styles";
import AgentAvatar from "./AgentAvatar";

const SEARCH_DELAY_MS = 300;
// No "usado em", os primeiros fluxos pelo nome; o resto vira "e mais N".
const USED_IN_SHOWN = 2;

export const usedInText = (usedIn) => {
  if (!usedIn || usedIn.length === 0) return i18n.t("agentManager.unused");
  const names = usedIn.map((flow) => (flow.isActive ? flow.title : `${flow.title} (${i18n.t("agentManager.flowOff")})`));
  const list = names.slice(0, USED_IN_SHOWN).join(", ");
  return names.length > USED_IN_SHOWN
    ? i18n.t("agentManager.usedInMore", { list, count: names.length - USED_IN_SHOWN })
    : i18n.t("agentManager.usedIn", { list });
};

const AgentList = ({ canManage, onEdit, onNew }) => {
  const classes = useStyles();
  const agentClasses = useAgentStyles();
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [agents, setAgents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  // A busca espera a pessoa parar de digitar.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async (pageNumber, searchParam) => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-agents", { params: { pageNumber, searchParam } });
      const list = Array.isArray(data?.agents) ? data.agents : [];
      setAgents((current) => (pageNumber === 1 ? list : [...current, ...list]));
      setHasMore(!!data?.hasMore);
      setPage(pageNumber);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1, query);
  }, [load, query]);

  const remove = async () => {
    const agent = deleting;
    setBusy(true);
    try {
      await api.delete(`/ai-agents/${agent.id}`);
      toast.success(i18n.t("agentManager.deleted"));
      setDeleting(null);
      await load(1, query);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={classes.toolbar}>
        <input
          className={agentClasses.search}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={i18n.t("agentManager.search")}
          aria-label={i18n.t("agentManager.search")}
        />
        {canManage && (
          <div className={agentClasses.toolbarButtons}>
            <button type="button" className={classes.btnSecondary} onClick={() => history.push("/assistente-ia")}>
              <Sparkles /> {i18n.t("agentManager.assistant")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={onNew}>
              <Plus /> {i18n.t("agentManager.blank")}
            </button>
          </div>
        )}
      </div>

      {loading && agents.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <CircularProgress size={24} />
        </div>
      ) : agents.length === 0 ? (
        <div className={classes.empty}>
          {query ? (
            i18n.t("agentManager.noResults")
          ) : (
            <>
              <div className={classes.emptyTitle}>{i18n.t("agentManager.emptyTitle")}</div>
              {i18n.t("agentManager.empty")}
            </>
          )}
        </div>
      ) : (
        <div className={classes.rows}>
          {agents.map((agent) => {
            const inUse = (agent.usedIn || []).length > 0;
            const action = canManage ? i18n.t("agentManager.edit") : i18n.t("agentManager.view");
            return (
              <div key={agent.id} className={classes.row}>
                <div
                  className={`${classes.rowMain} ${agentClasses.rowLead}`}
                  onClick={() => onEdit(agent.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onEdit(agent.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <AgentAvatar name={agent.name} profilePicUrl={agent.profilePicUrl} />
                  <div style={{ minWidth: 0 }}>
                    <div className={classes.rowTitle}>
                      {agent.name}
                      {!agent.activeVersionId && <span className={`${classes.chip} ${classes.chipOutline}`}>{i18n.t("agentManager.noVersion")}</span>}
                    </div>
                    <div className={agentClasses.usedIn}>{usedInText(agent.usedIn)}</div>
                    <div className={classes.rowMeta}>
                      {agent.model || "gpt-5-mini"} · {i18n.t("agentManager.updatedAt", { date: formatDate(agent.updatedAt) })}
                    </div>
                  </div>
                </div>
                <div className={classes.rowActions}>
                  <Tooltip title={action}>
                    <button type="button" className={classes.iconBtn} onClick={() => onEdit(agent.id)} aria-label={action}>
                      {canManage ? <Pencil /> : <Eye />}
                    </button>
                  </Tooltip>
                  {canManage && (
                    <Tooltip title={inUse ? i18n.t("agentManager.deleteInUse") : i18n.t("agentManager.delete")}>
                      <span>
                        <button
                          type="button"
                          className={classes.iconBtn}
                          onClick={() => setDeleting(agent)}
                          disabled={inUse || busy}
                          aria-label={i18n.t("agentManager.delete")}
                        >
                          <Trash2 />
                        </button>
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className={classes.loadMore}>
          <button type="button" className={classes.btnSecondary} onClick={() => load(page + 1, query)} disabled={loading}>
            {i18n.t("agentManager.loadMore")}
          </button>
        </div>
      )}

      <ConfirmationModal
        title={i18n.t("agentManager.confirmDeleteTitle")}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        confirmLoading={busy}
      >
        {deleting ? i18n.t("agentManager.confirmDelete", { name: deleting.name }) : ""}
      </ConfirmationModal>
    </>
  );
};

export default AgentList;
